import useProcessTimeStampsNoOffset from 'hooks/conversion/useProcessTimeStampsNoOffset';
import useAuth from 'hooks/useAuth';
import { useEffect, useRef, useMemo } from 'react';
import { dispatch, useSelector } from 'store';
import { setUserConfig } from 'store/reducers/config';
import { setDialogOpen } from 'store/reducers/dialog';
import { setUnreadNotifications } from 'store/reducers/notifications';

export default function AllUsersGlobalListeners() {
  const { db, user } = useAuth();
  const processTimestamps = useProcessTimeStampsNoOffset();
  
  // Memoize the selector to prevent unnecessary re-renders
  const userConfig = useSelector((state) => state.config.userConfig);
  const notifiedAboutAccounts = useMemo(() => userConfig?.notifiedAboutAccounts, [userConfig]);
  
  const notificationShownRef = useRef(false);

  // Show notification for accounts with multiple privileges
  useEffect(() => {
    const accountTypes = user?.accountTypes;
    if (
      !accountTypes ||
      accountTypes.length < 2 ||
      notifiedAboutAccounts ||
      notificationShownRef.current
    )
      return;

    notificationShownRef.current = true;
    const accountTypesString = accountTypes
      ?.map((type) => type.charAt(0).toUpperCase() + type.slice(1))
      .join(', ');

    dispatch(
      setDialogOpen({
        dialogOpen: true,
        dialogButtons: ['Ok'],
        dialogContent: `Hello!  It looks like your account has more than one privilege: ${accountTypesString}. \n
        IMPORTANT: You can use the "View as" button in the upper right menu (where your name is) to switch between the different views.`
      })
    );
    
    // Avoid unnecessary database operations if user is not defined
    if (db && user?.contactId) {
      db.collection('contacts')
        .doc(user.contactId)
        .set({ userConfig: { notifiedAboutAccounts: true } }, { merge: true });
    }
  }, [db, notifiedAboutAccounts, user]);

  // Log user visit - throttled to prevent excessive writes
  useEffect(() => {
    if (!user?.contactId || !db) return;
    
    // Use local storage to throttle updates to once per session
    const lastLogKey = `last_app_load_log_${user.contactId}`;
    const lastLog = localStorage.getItem(lastLogKey);
    
    if (!lastLog) {
      db.collection('contacts').doc(user.contactId).update({
        lastAppLoad: new Date()
      });
      localStorage.setItem(lastLogKey, Date.now().toString());
    }
  }, [db, user]);

  // Listen for user config changes
  useEffect(() => {
    if (!db || !user?.contactId) return;
    
    const unsubscribe = db
      .collection('contacts')
      .doc(user.contactId)
      .onSnapshot((snap) => {
        if (snap.exists) {
          dispatch(setUserConfig(snap.data()?.userConfig || {}));
        }
      });

    return () => unsubscribe();
  }, [db, user]);

  // Listen for unread notifications
  useEffect(() => {
    if (!db || !user?.contactId) return;
    
    const unsubscribe = db
      .collection('contacts')
      .doc(user.contactId)
      .collection('notifications')
      .where('status', '==', 'unread')
      .orderBy('timestamp', 'desc')
      .limit(50)
      .onSnapshot((snap) => {
        const notifications = snap.docs.map((doc) => {
          const rawData = { ...doc.data(), id: doc.id };
          return processTimestamps(rawData);
        });
        
        dispatch(setUnreadNotifications(notifications));
      });

    return () => unsubscribe();
  }, [db, user, processTimestamps]);

  return null;
}
